import React, {FC} from 'react';
import {connect} from 'react-redux';

import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  isCommentActivity,
  makeGetAttachments,
  makeGetAuthorFromComment,
  makeGetCommentAnnotations,
  makeGetReferencedActivity,
  makeGetReferencedAuthor,
  makeGetReferencedComment,
  makeGetReferencedRecipient,
} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {AppState} from '../../../../../store/appState';
import {getActivities, getComments} from '../../../../../store/data/dataSelectors';
import {SynopsisPinnedActivityItem} from './synopsisHelpers';
import {SynopsisPinnedComment} from './synopsisPinnedComment';

/*
 * Props.
 */

interface SynopsisPinnedCommentContainerProps {
  item: SynopsisPinnedActivityItem;
  panelId: ViewerPanelId;
}

type SynopsisPinnedCommentStateProps = ResolveStateProps<typeof mapStateToProps>;
type SynopsisPinnedCommentContainerPublicProps = SynopsisPinnedCommentContainerProps &
  SynopsisPinnedCommentStateProps;

/*
 * Selectors.
 */

function getActivityId(_state: AppState, props: SynopsisPinnedCommentContainerProps) {
  return props.item.activityId;
}

function makeGetComment() {
  return createSelector([getActivityId, getActivities, getComments], (activityId, activities, comments) => {
    const activity = activities[activityId];
    if (!activity || !isCommentActivity(activity)) {
      return null;
    }

    return comments[activity.commentUid] ?? null;
  });
}

const SynopsisPinnedCommentComponent: FC<SynopsisPinnedCommentContainerPublicProps> = (props) => {
  if (!props.comment || !props.author) {
    return null;
  }

  return (
    <SynopsisPinnedComment
      item={props.item}
      comment={props.comment}
      author={props.author}
      referencedComment={props.referencedComment}
      referencedAuthor={props.referencedAuthor}
      referencedRecipient={props.referencedRecipient}
      attachments={props.attachments}
      annotations={props.annotations}
      panelId={props.panelId}
    />
  );
};

const mapStateToProps = makeMapStateToProps<SynopsisPinnedCommentContainerProps>()(() => {
  const getComment = makeGetComment();
  const getReferencedActivity = makeGetReferencedActivity(getComment);

  return {
    comment: getComment,
    author: makeGetAuthorFromComment(getComment),
    referencedAuthor: makeGetReferencedAuthor(getReferencedActivity),
    referencedComment: makeGetReferencedComment(getReferencedActivity),
    referencedRecipient: makeGetReferencedRecipient(getReferencedActivity),
    attachments: makeGetAttachments(getComment),
    annotations: makeGetCommentAnnotations(getActivityId),
  };
});

export const SynopsisPinnedCommentContainer = connect(mapStateToProps)(SynopsisPinnedCommentComponent);
